import { History } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { formatAuditAt } from '../utils/propertyEditLogic'
import type { PropertyEditAuditLogSectionProps } from '../types'

function AuditChangesCell({ notes }: { notes: unknown }) {
  if (notes == null) return <span className="text-slate-500">—</span>
  let parsed: unknown = notes
  if (typeof notes === 'string') {
    const t = notes.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        parsed = JSON.parse(t) as unknown
      } catch {
        return <span className="text-slate-800">{notes}</span>
      }
    } else {
      return <span className="text-slate-800">{notes}</span>
    }
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return <span className="text-slate-800">{String(parsed)}</span>
  }
  const entries = Object.entries(parsed as Record<string, unknown>)
  if (entries.length === 0) return <span className="text-slate-500">—</span>
  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => {
        let left = ''
        let right = ''
        if (Array.isArray(value) && value.length >= 2) {
          left = String(value[1] ?? '')
          right = String(value[0] ?? '')
        } else if (Array.isArray(value) && value.length === 1) {
          left = String(value[0] ?? '')
        } else if (value != null && typeof value === 'object' && !Array.isArray(value)) {
          const o = value as Record<string, unknown>
          left = String(o[1] ?? o.new ?? o.old ?? '')
          right = String(o[0] ?? o.old ?? o.new ?? '')
        } else {
          left = String(value ?? '')
        }
        return (
          <div key={key} className="text-[13px] leading-snug text-slate-700">
            <span className="font-medium text-slate-800">{key}</span>
            {' : '}
            <span>{left}</span>
            {right ? (
              <>
                {' => '}
                <span>{right}</span>
              </>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export function PropertyEditAuditLogSection({ auditRows }: PropertyEditAuditLogSectionProps) {
  if (auditRows.length === 0) return null

  return (
    <div
      id="property-audit-logs"
      data-testid="property-edit-audit-log-section"
      className="scroll-mt-20 border-t border-slate-100 bg-slate-50/50 px-3 py-4 sm:px-[30px] sm:py-6"
    >
      <SectionCard
        icon={History}
        title="Audit Logs"
        testId="property-edit-audit-log-card"
        titleTestId="property-edit-audit-log-title"
      >
        <p className="text-[13px] text-slate-500">Field-level changes for this property.</p>
        <div className="overflow-x-auto rounded-lg border border-slate-200/80">
          <table data-testid="property-edit-audit-log-table" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 text-[13px] font-semibold text-slate-600">Changes By</th>
                <th className="px-4 py-3 text-[13px] font-semibold text-slate-600">Changes Data</th>
                <th className="whitespace-nowrap px-4 py-3 text-[13px] font-semibold text-slate-600">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {auditRows.map((entry, idx) => (
                <tr
                  key={idx}
                  className={
                    idx % 2 === 1 ? 'bg-sky-50/35 hover:bg-sky-50/55' : 'bg-white hover:bg-slate-50/90'
                  }
                >
                  <td className="px-4 py-3 align-top font-medium text-slate-800">
                    {String(entry.updated_by ?? entry.user_name ?? entry.name ?? '—')}
                  </td>
                  <td className="min-w-[200px] max-w-xl px-4 py-3 align-top">
                    <AuditChangesCell
                      notes={
                        entry.notes ??
                        (entry as Record<string, unknown>).changes ??
                        (entry as Record<string, unknown>).change_data
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 align-top text-[13px] text-slate-600">
                    {formatAuditAt(entry.updated_at ?? entry.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  )
}
